import {ReactNode} from 'react'
import classNames from 'classnames'
import {ChevronRightIcon} from 'components'

interface SettingsMobileCardProps {
  children: ReactNode
  className?: string
  dataCy?: string
}

/** A square hairline card that groups rows, like the desktop tables. */
export function SettingsMobileCard({
  children,
  className,
  dataCy,
}: Readonly<SettingsMobileCardProps>): JSX.Element {
  return (
    <div
      className={classNames(
        'mx-4 mb-5 border-0.5 border-gray-400 overflow-hidden',
        className,
      )}
      data-cy={dataCy}
    >
      {children}
    </div>
  )
}

interface SettingsMobileRowProps {
  children: ReactNode
  onClick: () => void
  dataCy?: string
  ariaLabel?: string
  isFlashing?: boolean
}

/** A 56px tappable row inside a card. */
export function SettingsMobileRow({
  children,
  onClick,
  dataCy,
  ariaLabel,
  isFlashing,
}: Readonly<SettingsMobileRowProps>): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      data-cy={dataCy}
      aria-label={ariaLabel}
      data-flash={isFlashing || undefined}
      className="settings-mobile-row flex items-center gap-3 w-full min-h-14 py-2.5 pl-4 pr-3.5 border-b-0.5 border-gray-1500 last:border-b-0 text-left karla text-white touch-manipulation"
    >
      {children}
    </button>
  )
}

/** The trailing chevron of a row that opens something. */
export function SettingsMobileChevron(): JSX.Element {
  return (
    <span className="flex flex-shrink-0" aria-hidden="true">
      <ChevronRightIcon
        width={7}
        height={12}
        strokeColor="text-gray-700"
        strokeWidth={1.5}
      />
    </span>
  )
}

interface SettingsMobileIconButtonProps {
  label: string
  onClick: () => void
  children: ReactNode
  dataCy?: string
  className?: string
}

/** A round 44px icon button, such as the ⊕ beside a page title. */
export function SettingsMobileIconButton({
  label,
  onClick,
  children,
  dataCy,
  className,
}: Readonly<SettingsMobileIconButtonProps>): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      data-cy={dataCy}
      className={classNames(
        'settings-mobile-press flex flex-shrink-0 items-center justify-center w-11 h-11 rounded-ds-pill text-white active:bg-white/10 touch-manipulation',
        className,
      )}
    >
      {children}
    </button>
  )
}
